import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Typography,
} from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { userService } from '../../services/userService';
import { teamService } from '../../services/teamService';
import { User } from '../../types';
import ActivityDashboard from '../activities/ActivityDashboard';

const PublicDashboardPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [profileUser, setProfileUser] = useState<User | null>(null);
  const [targetTeamId, setTargetTeamId] = useState<string | null>(null);

  useEffect(() => {
    const loadDashboard = async () => {
      if (!userId) {
        setError('Invalid dashboard URL');
        setLoading(false);
        return;
      }

      const viewerId = currentUser?.id || 'anonymous';

      try {
        setLoading(true);
        setError('');

        const profileResult = await userService.getPublicProfile(userId, viewerId);
        if (!profileResult.canView || !profileResult.user) {
          setError(profileResult.reason || 'You do not have permission to view this dashboard');
          return;
        }

        const viewedUser = profileResult.user;
        setProfileUser(viewedUser);

        const [athleteTeams, coachTeams] = await Promise.all([
          teamService.getTeamsByAthlete(viewedUser.id),
          teamService.getTeamsByCoach(viewedUser.id),
        ]);
        const allTeams = [...athleteTeams];
        coachTeams.forEach((team) => {
          if (!allTeams.some((existing) => existing.id === team.id)) {
            allTeams.push(team);
          }
        });
        setTargetTeamId(allTeams[0]?.id || null);
      } catch (err: any) {
        console.error('Error loading public dashboard:', err);
        setError(err.message || 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, [userId, currentUser?.id]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate(-1)} variant="outlined">
          Go Back
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate(`/profile/${userId}`)}>
          Back to Profile
        </Button>
        {currentUser && (
          <Button variant="outlined" onClick={() => navigate('/?page=teams')}>
            Back to Team Roster
          </Button>
        )}
      </Box>

      <Typography variant="h4" component="h1" gutterBottom>
        {profileUser?.displayName}'s Dashboard
      </Typography>
      {targetTeamId ? (
        <ActivityDashboard
          teamId={targetTeamId}
          targetUserId={profileUser?.id}
          readOnly
          readOnlyHeaderText={`Viewing ${profileUser?.displayName}'s dashboard. Data entry and edits are disabled.`}
        />
      ) : (
        <Alert severity="info" sx={{ mt: 2 }}>
          No team is associated with this user yet, so dashboard data is unavailable.
        </Alert>
      )}
    </Container>
  );
};

export default PublicDashboardPage;
